import type { User } from "@/types";
import type { NvGnbUserType } from "@/components/navigator/nv-gnb";
import { getApiError } from "@/lib/api-error";

// Front access levels decided by the server (vault "설계/권한 모델"): GET /users/me → access_level.
export type AccessLevel = "guest" | "pending" | "demand" | "supplier" | "internal";

const ACCESS_LEVELS: readonly string[] = ["guest", "pending", "demand", "supplier", "internal"];

export function isAccessLevel(value: unknown): value is AccessLevel {
  return typeof value === "string" && ACCESS_LEVELS.includes(value);
}

// Fails closed: only the server-decided access_level counts. A missing or unknown value (older response,
// new level this build does not know) is treated as a guest, never as a member — user_type is not used to guess.
export function accessLevelOf(user: User | null | undefined): AccessLevel {
  const level: unknown = user?.access_level;
  return isAccessLevel(level) ? level : "guest";
}

// 로그인한 회원 등급 (수요자 · 공급자 · PwC 내부) — 승인 대기 · 비회원은 아니에요
export function isMemberLevel(level: AccessLevel): boolean {
  return level === "demand" || level === "supplier" || level === "internal";
}

// GNB 4종: 승인 대기는 비회원 헤더 (Front 화면설계서 slide 23)
export function gnbTypeOf(level: AccessLevel): NvGnbUserType {
  switch (level) {
    case "demand":
      return "buyer";
    case "supplier":
      return "supplier";
    case "internal":
      return "pwc";
    default:
      return "guest";
  }
}

// 상세 · Agent Finder 기능을 못 쓰는 등급 → 이용 권한 안내 사유 코드 (서버 DENIAL_CODES와 같은 값)
// 화면의 이용 권한 안내 팝업(NvAccessDialog)도 이 타입 하나만 써요.
export type AccessDenialReason = "LOGIN_REQUIRED" | "PENDING_APPROVAL" | "USER_TYPE_FORBIDDEN";

const ACCESS_DENIAL_REASONS: readonly string[] = ["LOGIN_REQUIRED", "PENDING_APPROVAL", "USER_TYPE_FORBIDDEN"];

// 서버가 이용 권한 때문에 거절한 응답의 오류 코드인가 (401 LOGIN_REQUIRED · 403 PENDING_APPROVAL / USER_TYPE_FORBIDDEN)
export function isAccessDenial(code: unknown): code is AccessDenialReason {
  return typeof code === "string" && ACCESS_DENIAL_REASONS.includes(code);
}

// 실패한 요청(axios 오류)이 이용 권한 거절이면 그 사유, 아니면 null
export function accessDenialOf(error: unknown): AccessDenialReason | null {
  const code = getApiError(error)?.code;
  return isAccessDenial(code) ? code : null;
}

export function accessReasonOf(level: AccessLevel): AccessDenialReason | null {
  if (level === "guest") return "LOGIN_REQUIRED";
  if (level === "pending") return "PENDING_APPROVAL";
  if (level === "supplier") return "USER_TYPE_FORBIDDEN";
  return null;
}
